import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Select } from '@components/Select';
import { useTranslation } from '@hooks';
import { teamsService } from '@services';

const hasMessagingAccess = team => team.permissions?.some(p => p === 'messaging:restricted' || p === 'messaging:full');

const TeamSelect = ({ data, disabled }) => {
    const [teams, setTeams] = useState([]);

    const { control, setValue, watch } = useFormContext();
    const { t } = useTranslation();

    const teamAlreadySetToForm = watch('teamAlreadySetToForm');

    useEffect(() => {
        teamsService
            .getTeams()
            .then(setTeams)
            .catch(() => setTeams([]));
    }, []);

    useEffect(() => {
        if (data && !teamAlreadySetToForm) {
            setValue('teamId', data?.teamId || '', { shouldValidate: true });
            setValue('teamAlreadySetToForm', true);
        }
    }, [data, teamAlreadySetToForm, setValue]);

    const options = useMemo(() => {
        if (!teams.length) {
            return data?.teamId && data?.teamName ? [{ value: data.teamId, label: data.teamName }] : [];
        }
        return teams
            .filter(team => hasMessagingAccess(team) || team.id === data?.teamId)
            .map(team => ({ value: team.id, label: team.name }));
    }, [teams, data?.teamId, data?.teamName]);

    return (
        <div className="team-select">
            <Controller
                control={control}
                name="teamId"
                render={({ field, fieldState: { error }, ref }) => (
                    <Select
                        {...field}
                        ref={ref}
                        onChange={({ target: { value } }) => {
                            setValue('teamId', value, { shouldValidate: true, shouldDirty: true });
                        }}
                        options={options}
                        required
                        label={t('common.team')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        disabled={disabled}
                    />
                )}
            />
        </div>
    );
};

export default TeamSelect;
